import { ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const Eyebrow = ({ children, light }) => <p className={`text-[11px] font-semibold tracking-[0.25em] uppercase mb-4 ${light ? 'text-teal-300' : 'text-teal-700'}`}>{children}</p>;

export const PartnerHero = ({ eyebrow, title, accent, text, cta, ctaHref, secondary, secondaryHref, panel, testid }) => (
  <section className="relative bg-[#0b1f4d] text-white overflow-hidden" data-testid={testid}>
    <div className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full bg-teal-400/10 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
      <div className="animate-in fade-in slide-in-from-bottom-3 duration-700">
        <Eyebrow light>{eyebrow}</Eyebrow>
        <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{title} <span className="text-teal-300">{accent}</span></h1>
        <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">{text}</p>
        <div className="mt-9 flex flex-wrap gap-4">
          <a href={ctaHref} data-testid={`${testid}-cta`} className="group inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full transition-colors">{cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></a>
          {secondary && <a href={secondaryHref} data-testid={`${testid}-secondary`} className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors">{secondary}</a>}
        </div>
      </div>
      <div className="animate-in fade-in slide-in-from-right-4 duration-700">{panel}</div>
    </div>
  </section>
);

export const ValueGrid = ({ eyebrow, title, text, items, dark, testid, cols = 'lg:grid-cols-4' }) => (
  <section className={dark ? 'bg-[#0b1f4d] text-white' : 'bg-white'} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <Eyebrow light={dark}>{eyebrow}</Eyebrow>
      <h2 className={`font-outfit font-bold text-3xl sm:text-4xl max-w-3xl leading-tight ${dark ? '' : 'text-[#0b1f4d]'}`}>{title}</h2>
      {text && <p className={`mt-4 max-w-2xl leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{text}</p>}
      <div className={`mt-12 grid sm:grid-cols-2 ${cols} gap-5`}>
        {items.map(([Icon, t, d, tag], i) => (
          <div key={t} style={{ animationDelay: `${i * 90}ms` }} data-testid={`${testid}-item-${i}`}
            className={`group rounded-3xl p-7 animate-in fade-in slide-in-from-bottom-2 duration-700 fill-mode-both transition-[transform,border-color] hover:-translate-y-1 ${dark ? 'bg-white/[0.04] border border-white/10 hover:border-teal-400/40' : 'bg-slate-50 border border-slate-200 hover:border-teal-300'}`}>
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${dark ? 'bg-teal-400/15' : 'bg-white border border-slate-200'}`}><Icon className={`w-5 h-5 ${dark ? 'text-teal-300' : 'text-teal-700'}`} /></span>
            {tag && <p className={`text-[11px] uppercase tracking-widest mt-5 ${dark ? 'text-teal-300' : 'text-teal-700'}`}>{tag}</p>}
            <p className={`font-outfit text-lg font-semibold ${tag ? 'mt-1' : 'mt-5'} ${dark ? '' : 'text-[#0b1f4d]'}`}>{t}</p>
            <p className={`text-sm mt-2 leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Steps = ({ eyebrow, title, steps, testid }) => (
  <section className="bg-slate-50" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-[#0b1f4d] max-w-2xl leading-tight">{title}</h2>
      <ol className="mt-12 grid md:grid-cols-4 gap-5 relative">
        <span className="hidden md:block absolute top-6 left-6 right-6 h-px bg-slate-300" />
        {steps.map(([t, d], i) => (
          <li key={t} className="relative" data-testid={`${testid}-step-${i}`}>
            <span className="relative z-10 w-12 h-12 rounded-full bg-[#0b1f4d] text-teal-300 font-outfit font-bold flex items-center justify-center">{i + 1}</span>
            <p className="font-outfit text-lg font-semibold text-[#0b1f4d] mt-5">{t}</p>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const Compare = ({ eyebrow, title, left, right, rows, testid }) => (
  <section className="bg-white" data-testid={testid}>
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-[#0b1f4d] leading-tight">{title}</h2>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <div className="rounded-3xl border border-slate-200 p-7"><p className="text-xs uppercase tracking-widest text-slate-500">{left}</p>
          <ul className="mt-5 space-y-3">{rows.map(([a]) => <li key={a} className="text-slate-500 line-through decoration-slate-300">{a}</li>)}</ul></div>
        <div className="rounded-3xl bg-[#0b1f4d] text-white p-7"><p className="text-xs uppercase tracking-widest text-teal-300">{right}</p>
          <ul className="mt-5 space-y-3">{rows.map(([, b]) => <li key={b} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full bg-teal-300 mt-2.5 shrink-0" />{b}</li>)}</ul></div>
      </div>
    </div>
  </section>
);

export const MiniFaq = ({ items, testid }) => (
  <section className="bg-slate-50" data-testid={testid}>
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
      <h2 className="font-outfit font-bold text-3xl text-[#0b1f4d] mb-8">Common questions</h2>
      <Accordion type="single" collapsible className="rounded-2xl bg-white border border-slate-200 px-5">
        {items.map(([q, a], i) => (
          <AccordionItem key={q} value={q} className={i === items.length - 1 ? 'border-b-0' : ''}>
            <AccordionTrigger className="text-left font-semibold text-[#0b1f4d] hover:no-underline" data-testid={`${testid}-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const SamplePanel = ({ label, title, children }) => (
  <div className="relative rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-md p-6 shadow-2xl">
    <div className="flex items-center justify-between"><p className="font-outfit font-semibold">{title}</p><span className="text-[10px] uppercase tracking-widest text-slate-400 border border-white/15 rounded-full px-2 py-0.5">{label}</span></div>
    <div className="mt-5">{children}</div>
  </div>
);

export const Bar = ({ label, value }) => (
  <div className="mb-3"><div className="flex justify-between text-xs text-slate-300 mb-1"><span>{label}</span><span>{value}%</span></div>
    <div className="h-2 rounded-full bg-white/10"><div className="h-2 rounded-full bg-teal-400" style={{ width: `${value}%` }} /></div></div>
);
